import React, { createContext, useCallback, useContext, useState } from "react";
import { randomId } from "../utils/browserSafety";

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]); // {id, title, desc, variant}

  const push = useCallback(
    ({ title, desc, variant = "default", duration = 3500 }) => {
      const id = randomId();
      setToasts((t) => [...t, { id, title, desc, variant }]);
      if (duration) {
        setTimeout(
          () => setToasts((t) => t.filter((x) => x.id !== id)),
          duration
        );
      }
    },
    []
  );

  const remove = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  return (
    <ToastCtx.Provider value={{ push, remove }}>
      {children}
      {/* Portal container */}
      <div
        className="fixed z-[9999] top-4 right-4 space-y-2"
        role="region"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((t) => (
          <Toast key={t.id} {...t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

function Toast({ title, desc, variant = "default", onClose }) {
  var styles = ""
  switch (variant) {
    case "success":
      styles = "border-green-200 bg-green-50 text-green-800"
      break;
    case "error":
      styles = "border-red-200 bg-red-50 text-red-800"
      break;
    case "danger":
      styles = "border-red-300 bg-red-100 text-red-800"
      break;
    case "info":
      styles = "border-yellow-200 bg-yellow-50 text-yellow-800"
      break;
    default:
      styles = "border-slate-200 bg-white text-slate-900"

  }

  return (
    <div className={`w-80 rounded-xl border shadow-sm p-3 ${styles}`}>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{/* Place an icon here if you like */}</div>
        <div className="flex-1">
          {title && <div className="text-sm font-semibold">{title}</div>}
          {desc && <div className="text-xs opacity-80 mt-0.5">{desc}</div>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="ml-2 px-2 py-1 text-xs rounded hover:bg-black/5"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
